import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Alert
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "@/hooks";
import {
  fetchAllCases,
  setSelectedCase,
  getDocumentCategories,
  selectCasesState,
} from "@/redux/casesSlice";
import { columns } from "./Table.data";
import Table from "../Table";
import { ICase, ITableFilter } from "@/redux/store.types";
import { useNavigate } from "react-router-dom";

const MyCasesTableWrapper = () => {
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const state = useAppSelector<any>(selectCasesState);
  const { data, pagination, isLoading } = state;
  const [selectedColumn, setSelectedColumn] = useState<number | string>(0);
  const [searchString, setSearchString] = useState<string>("");
  const [thunk, setThunk] = useState<any>(null);

  const [params, setParams] = useState<any>(null);

  const onSelectCase = (data: ICase) => {
    appDispatch(setSelectedCase(data));
    navigate(`/case/${data.DeliveryEngagementId}`);
  };

  const onChangeFilters = (filter: ITableFilter[]) => {
    const args = { ...params };
    if (filter[0].value !== "") {
      args["filter"] = { items: filter };
    } else {
      delete args["filter"];
    }
    setParams(args);
  };

  const onFilterTable = () => {
    const args: any = {
      skip: 0,
      top: 20,
      sort: "",
      include: "*",
    };

    thunk && thunk.abort();
    setThunk(null);

    if (searchString === "" && params.filter) {
      delete args.filter;
    } else if (searchString !== "") {
      args["filter"] = {
        items: [
          {
            field: columns[Number(selectedColumn)].field,
            operator: "contains",
            value: searchString,
          },
        ],
      };
    }
    setParams(args);
  };

  const onSortChange = (sort: { sort: string }) => {
    const args = { ...params };
    if (sort.sort) {
      args.sort = sort.sort;
    } else {
      args.sort = "";
    }
    setParams(args);
  };

  const onPaginationChange = (pagination: number) => {
    const args = { ...params };
    args.skip = pagination;
    setParams(args);
  };

  useEffect(() => {
    if (!params) return;
    const cancable = appDispatch(fetchAllCases(params));
    setThunk(cancable);
  }, [params, appDispatch]);

  useEffect(() => {
    appDispatch(getDocumentCategories());
    //appDispatch(getCasePrivicyLevels());
    //appDispatch(getDocumentPrivicyLevels());
    //appDispatch(getIndustries());
    //appDispatch(getMethodologies());
  }, []);

  const isDisabled = () => {
    if (searchString === "") {
      if (params?.filter) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      <Typography data-testid="page-title" variant="h5">
        My Cases
      </Typography>
      <Box sx={{ display: "flex", gap: "10px" }}>
        <TextField
          label="Search Project Code"
          data-testid="search-input"
          sx={{
            minWidth: 300,
            "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
              backgroundColor: "white",
              paddingRight: "8px",
            },
          }}
          onChange={(e) => {
            setSearchString(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && searchString !== "") {
              onFilterTable();
            }
          }}
          value={searchString}
        />

        <Button
          size="small"
          variant="contained"
          disableElevation
          onClick={onFilterTable}
          disabled={isDisabled()}
          data-testid="search-button"
        >
          Search
        </Button>
        <Button
          variant="text"
          data-testid="clear-button"
          onClick={() => {
            thunk && thunk.abort();
            setThunk(null);
            const args = { ...params };
            delete args.filter;
            setParams(null);
            setSearchString("");
            setSelectedColumn(1);
          }}
          disabled={searchString === "" || isLoading || !params?.filter}
        >
          Clear
        </Button>
      </Box>
      {!data && isLoading && (
        <Box
          sx={{
            height: 800,
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
          data-testid="loading-spinner"
        >
          <CircularProgress />
        </Box>
      )}
      {!data && !isLoading && (
        <Box
          sx={{
            width: "60%",
            display: "flex",
          }}
        >
          <Alert severity="info">
            Search your project code to start debriefing a case.
            <ol style={{paddingLeft: '15px'}}>
              <li>
                Enter the P code in the search bar and click Search. Before searching, check the P-code field for extra spaces, especially trailing spaces after the code. Vault will not find the P-code if a space is present.
              </li>
              <li>

                Open the case once it appears.
              </li>
              <li>

                Upload your files by dragging and dropping them or using Browse.
              </li>
              <li>

                Tag each file correctly, for example Qual, Proposal, or Project material.
              </li>

              <li>
                If your file is already sanitized, you can mark it as such.

              </li>
              <li>

                When you are done, close the page and your files will appear in Activities while they are processed.
              </li>

            </ol>

            If you do not have a qual yet, create it in <a href="https://lab.adlittle.com/">Lab - Slide Agent - Qual builder
            </a> first.
            For any issue, contact <a href="mailto:Knowledge@adlittle.com" >Knowledge@adlittle.com</a>.
          </Alert>

        </Box >
      )}
      {
        params && (
          <Table
            testId="my-cases-table"
            height={800}
            data={data}
            columns={columns}
            totalPages={pagination.total}
            pageSize={pagination.top}
            pageNumber={pagination.skip / pagination.top}
            onSelectRow={onSelectCase}
            isLoading={isLoading}
            onFilterChange={onChangeFilters}
            onSortChange={onSortChange}
            onPaginationChange={onPaginationChange}
          />
        )
      }
    </>
  );
};

export default MyCasesTableWrapper;
